import Tooltip from 'modules/shared-components/tooltip';
import Big from 'big.js';
import { calculateLimits } from '../../components/TokenTradeInfoHeader';
import { formatNumberWithSuffix } from 'utils/FormatNumber';
import moment from 'moment';
import { ContractInfo } from 'api/types/httpsTypes/contracts';
import { styled } from '@slicknode/stylemapper';
import { chainHasTax, Chains } from 'api/types/httpsTypes/d-wallets';

export const ListItem = styled(
  'li',
  'xxs:flex xxs:justify-between xxs:items-center xxs:gap-2 xxs:text-sm'
);
export const ListItemLabel = styled(
  'div',
  'xxs:text-dex-white-secondary whitespace-nowrap py-0.5 flex items-center xxs:gap-1 xxs:text-xs'
);
export const ListItemValue = styled(
  'div',
  'dark:text-white whitespace-nowrap truncate xxs:overflow-y-visible py-0.5 xxs:text-xs max-w-[200px] truncate'
);

interface Props {
  contract: ContractInfo;
}

export const TradingInfo = ({ contract }: Props) => {
  const { tradingInfo, generalInfo } = contract;
  const { key: chainKey } = generalInfo.chain;

  const tradingInfoList = [
    {
      title: 'Taxes',
      tooltip: 'The tax applied to any buy / sell orders.',
      value:
        tradingInfo.buyTax || tradingInfo.sellTax ? (
          <div className="font-bold">
            B:{' '}
            {tradingInfo.buyTax ? (
              <span>{Big(tradingInfo.buyTax).times(100).toFixed(1)}%</span>
            ) : (
              '--'
            )}{' '}
            | S:{' '}
            {tradingInfo.sellTax ? (
              <span
                className={
                  Big(tradingInfo.sellTax).gt(0.1) ? 'text-red-500' : ''
                }
              >
                {Big(tradingInfo.sellTax).times(100).toFixed(1)}%
              </span>
            ) : (
              '--'
            )}
          </div>
        ) : (
          '--'
        ),
      condition: chainHasTax(chainKey),
    },
    {
      title: 'Max Limits',
      tooltip:
        'The maximum amount of tokens a wallet address can buy / sell / hold.',
      value:
        tradingInfo.maxBuy || tradingInfo.maxSell || tradingInfo.maxWallet ? (
          <div className="font-bold">
            B{' '}
            {calculateLimits({
              totalSupply: generalInfo.totalSupply,
              max: tradingInfo.maxBuy,
            })}{' '}
            | S{' '}
            {calculateLimits({
              totalSupply: generalInfo.totalSupply,
              max: tradingInfo.maxSell,
            })}{' '}
            | W{' '}
            {calculateLimits({
              totalSupply: generalInfo.totalSupply,
              max: tradingInfo.maxWallet,
            })}
          </div>
        ) : (
          '--'
        ),
      condition: true,
    },
    {
      title: 'Total Supply',
      tooltip: 'The total supply of tokens.',
      value: generalInfo.totalSupply
        ? `${formatNumberWithSuffix(generalInfo.totalSupply)} ${
            generalInfo.symbol
          }`
        : '--',
      condition: true,
    },
    {
      title: 'Decimals',
      tooltip: 'The decimals of the contract.',
      value: generalInfo.decimals ?? '--',
      condition: true,
    },
    {
      title: 'Creation Date',
      tooltip: 'The deployment date and time of the contract.',
      value: generalInfo.creationDatetime ? (
        <span className="font-bold">
          {moment(generalInfo.creationDatetime).format('DD/MM/YYYY HH:mm:ss')}
        </span>
      ) : (
        '--'
      ),
      condition: true,
    },
    {
      title: 'Is Token Data Mutable?',
      tooltip: 'Check if token data is mutable.',
      value: tradingInfo.mutable ? (
        <div className="font-bold">YES</div>
      ) : (
        <div className="font-bold">NO</div>
      ),
      condition: chainKey === Chains.Solana,
    },
    {
      title: 'Is Mintable?',
      tooltip: 'Check if mint authority is null.',
      value: tradingInfo.mutable ? (
        <div className="font-bold">YES</div>
      ) : (
        <div className="font-bold">NO</div>
      ),
      condition: chainKey === Chains.Solana,
    },
    {
      title: 'Is Freezable?',
      tooltip: 'Check if freeze authority is null.',
      value: tradingInfo.freezable ? (
        <div className="font-bold">YES</div>
      ) : (
        <div className="font-bold">NO</div>
      ),
      condition: chainKey === Chains.Solana,
    },
    {
      title: 'Update Authority',
      tooltip: 'Update Authority.',
      value: tradingInfo.updateAuthority ? (
        <div className="font-bold">YES</div>
      ) : (
        <div className="font-bold">NO</div>
      ),
      condition: chainKey === Chains.Solana,
    },
  ];

  return (
    <div className="xxs:dark:bg-dex-black-700 xxs:p-2 xxs:rounded w-full space-y-1">
      <div className="hidden lg:block font-bold text-sm">Trading Info</div>

      {tradingInfoList
        .filter((item) => item.condition) // Filter out items with false conditions
        .map((item, index) => (
          <ListItem key={index}>
            <ListItemLabel>
              <Tooltip text={item.tooltip}>
                <div className="xxs:text-dex-white-secondary hover:text-dex-white">
                  {item.title}
                </div>
              </Tooltip>
            </ListItemLabel>
            <ListItemValue>{item.value}</ListItemValue>
          </ListItem>
        ))}
    </div>
  );
};
